import axios from 'axios'
import { v4 } from 'uuid'
import { lib } from '@gocanvas/client'
const { getValue, getOperationType, conditionCheck, LOOP_SHEET_TYPE_IDS } = lib
const { get, post } = axios

const getToken = () => document.querySelector('[name="csrf-token"]').content
const buildHeaders = () => ({
  headers: {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getToken(),
  },
})

export const csrfFetch = url => get(url, buildHeaders())

export const fetchConditionTypes = () => csrfFetch('/condition_types.json')

export const getForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=flat`

  return get(formEndpoint)
}

export const getWebForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=web_form`

  return get(formEndpoint)
}

export const getReferenceData = async form_id => {
  const refDataEndpoint = `/api/v3/reference_data?form_id=${form_id}`

  return get(refDataEndpoint)
}

export const getOnboardingReferenceData = async form_id => {
  const refDataEndpoint = `/reference_datas.json?form_id=${form_id}`

  return get(refDataEndpoint).then(response => {
    const { data } = response

    return {
      data: data.map(file => ({
        ...file,
        headers: file.reference_columns, // Ensure each file has a unique content_guid
      })),
    }
  })
}

export const fetchFormData = async formId => {
  const { initialOnboardingState = {} } = window || {}
  const { user = {} } = initialOnboardingState
  const { appId } = user
  const id = formId || appId

  if (id) {
    const [formResponse, conditionTypesResponse, refDataResponse] =
      await Promise.all([
        getForm(id),
        fetchConditionTypes(),
        getReferenceData(id),
      ])

    const { data: formData } = formResponse
    const { data: conditionTypes } = conditionTypesResponse
    const { data: refData } = refDataResponse

    const { entities } = unpackFlatApp(formData)

    return {
      ...entities,
      conditionTypes: conditionTypes.map(unpackConditionType),
      refData,
    }
  } else {
    throw new Error('No formId or appId provided')
  }
}

export const fetchOnboardingFormData = async formId => {
  const { initialOnboardingState = {} } = window || {}
  const { user = {} } = initialOnboardingState
  const { appId } = user
  const id = formId || appId

  if (id) {
    const [formResponse, conditionTypesResponse, refDataResponse] =
      await Promise.all([
        getForm(id),
        fetchConditionTypes(),
        getOnboardingReferenceData(id),
      ])

    const { data: formData } = formResponse
    const { data: conditionTypes } = conditionTypesResponse
    const { data: refData } = refDataResponse

    const { entities } = unpackFlatApp(formData)

    return {
      ...entities,
      conditionTypes: conditionTypes.map(unpackConditionType),
      refData,
    }
  } else {
    throw new Error('No formId or appId provided')
  }
}

export const fetchWebFormData = async () => {
  const { initialOnboardingState = {} } = window || {}
  const { appId, webFormLogo } = initialOnboardingState

  if (appId) {
    const { data: formData } = await getWebForm(appId)
    const { data: conditionTypes } = await fetchConditionTypes()
    const { entities } = unpackFlatApp(formData)
    const { app } = entities
    return {
      entities: {
        ...entities,
        app: {
          ...app,
          logo: webFormLogo,
        },
      },
      types: {
        conditionTypes: conditionTypes.map(unpackConditionType),
      },
    }
  }
}

const linkConditions = (conditions, entries) => {
  return conditions.map(({ entry_id, parent_entry_id, ...condition }) => {
    const parentEntry = entries.find(({ id }) => id === parent_entry_id) || {}
    return {
      parentEntryGuid: parentEntry.guid,
      ...condition,
    }
  })
}

const linkOperations = operations => {
  return operations.map(({ operation_type_id, ...operation }, i) => {
    return {
      operation_type_id,
      operation_type: getOperationType(operation_type_id),
      index: i,
      ...operation,
    }
  })
}

const linkMultiSection = sheets =>
  sheets.map(({ multi_section, ...sheet }) => ({
    ...sheet,
    multi_section: multi_section ? multi_section.id : multi_section,
  }))

const normalizeEntryTypes = entry => {
  const type = entry.type == 'Summary' ? 'Sum' : entry.type
  return {
    ...entry,
    type,
  }
}

export const unpackFlatApp = ({
  operations,
  conditions,
  entries,
  sheets,
  sections,
  ...app
}) => {
  const normalizedEntries = entries.map(normalizeEntryTypes)
  return {
    entities: {
      operations: linkOperations(operations),
      conditions: linkConditions(conditions, normalizedEntries),
      entries: normalizedEntries,
      sheets: linkMultiSection(sheets),
      sections,
      app,
    },
  }
}

export const unpackConditionType = ({ condition_type }) => condition_type

const fieldTypeTransforms = entry => {
  const { type, value, length, ...rest } = entry

  // image fields should create a content_guid, later used as a reference
  if (['Signature', 'Drawing'].includes(type) && value) {
    return {
      type,
      ...rest,
      value,
      content_guid: v4(),
    }
  }

  if (type === 'Photo') {
    if (value && value.length) {
      if (length > 1) {
        return {
          type,
          ...rest,
          content_guids: value.map(({ url }) => ({ url, content_guid: v4() })),
        }
      } else {
        return {
          type,
          ...rest,
          value: value[0].url,
          content_guid: v4(),
        }
      }
    }
  }

  if (type === 'Video' || type === 'Attachment') {
    if (value && value.length) {
      if (length > 1) {
        return {
          type,
          ...rest,
          content_guids: value.map(({ url, name }) => ({
            url,
            content_guid: v4(),
            filename: name,
          })),
        }
      } else {
        return {
          type,
          ...rest,
          value: value[0].url,
          content_guid: v4(),
          filename: value[0].name,
        }
      }
    }
  }

  return {
    ...entry,
    value: getValue(type, value),
  }
}

const fetchBlobAppend = (formData, content_guid, value, filename) =>
  fetch(value)
    .then(response => response.blob())
    .then(file => {
      // Create a File object with the proper filename
      const fileWithName = new File([file], filename || 'file', {
        type: file.type,
      })
      formData.append(content_guid, fileWithName)
    })

// extracts any files and appends them to the form
async function extractFiles(formData, transformedData) {
  await Promise.all(
    transformedData
      .filter(
        ({ content_guid, content_guids }) => content_guid || content_guids
      )
      .flatMap(({ content_guid, content_guids, value, filename }) => {
        if (content_guid) {
          return [fetchBlobAppend(formData, content_guid, value, filename)]
        } else if (content_guids && content_guids.length) {
          return content_guids.map(({ url, content_guid, filename }) =>
            fetchBlobAppend(formData, content_guid, url, filename)
          )
        }
      })
  )
  return formData
}

// entries with content_guid should not have values, and vice-versa
const filterFieldProps = ({
  id,
  value,
  displayed,
  content_guid,
  content_guids,
  multi_key,
}) => {
  const field = {
    entry_id: id,
    displayed,
    multi_key,
  }
  if (content_guid) {
    return { ...field, content_guid }
  } else if (content_guids) {
    return {
      ...field,
      content_guids: content_guids.map(({ content_guid }) => content_guid),
    }
  }
  return { ...field, value }
}

export const annotateValidPreviewEntries = ({
  previewEntries,
  validSheetIds,
  conditions,
}) =>
  previewEntries.map(entry => {
    const { sheet_id, rowGuid, parentGuid, guid } = entry
    const displayed =
      validSheetIds.includes(sheet_id) &&
      conditionCheck(
        parentGuid || guid,
        previewEntries,
        conditions,
        rowGuid || false
      )
    return { ...entry, displayed }
  })

export const assembleSubmission = async (
  { formVersion, formId, guid, finalize, submission_name, lastSheetId },
  data, webclientsub = 'web-client',
  recipients = []
) => {
  const formData = new FormData()
  const shouldFinalize = finalize ?? true

  // eventually edits will need to pass this in
  formData.append('guid', v4())

  formData.append('finalize', shouldFinalize)
  formData.append('webclientsub', webclientsub)

  if (!shouldFinalize) {
    formData.append('submission_name', submission_name)
    formData.append('LastSheetID', lastSheetId)
  }

  formData.append(
    'form',
    JSON.stringify({
      id: formId,
      guid,
      version: formVersion,
      type: 'form',
      webclientsub,
    })
  )
  formData.append('recipients', JSON.stringify(recipients))

  const associateLoops = data => {
    return data.map(
      ({
        parentGuid,
        keyfieldGuid,
        rowGuid,
        nestedRowGuid,
        keyField,
        ...entry
      }) => {
        if (parentGuid && parentGuid !== keyfieldGuid) {
          const parentEntry = data.find(
            parentEntry =>
              (parentEntry.rowGuid == rowGuid ||
                parentEntry.nestedRowGuid == rowGuid) &&
              parentEntry.parentGuid == keyfieldGuid
          )

          return fieldTypeTransforms({
            multi_key: parentEntry.value,
            ...entry,
          })
        }

        return fieldTypeTransforms(entry)
      }
    )
  }

  const transformedData = associateLoops(data)

  formData.append(
    'responses',
    JSON.stringify(transformedData.map(filterFieldProps))
  )

  return await extractFiles(formData, transformedData)
}

export const createAPISubmission = async ({
  formId,
  formVersion,
  finalize = true,
  submission_name = '',
  lastSheetId,
  previewEntries,
  recipients = [],
  webclientsub = 'web-client',
}) => {
  const submissionEndpoint = '/api/v3/submissions'
  const formMeta = {
    formId,
    formVersion,
    finalize,
    submission_name,
    lastSheetId,
  }

  const submissionData = await assembleSubmission(
    formMeta,
    previewEntries,
    webclientsub,
    recipients
  )
  return post(submissionEndpoint, submissionData)
}

export const findChildSheetIds = (sheets, multiSectionId) => {
  const children = sheets
    .filter(sheet => sheet.section_id === multiSectionId)
    .map(({ id }) => id)
  const grandChildren = children
    .filter(({ multi_section }) => multi_section)
    .flatMap(child => findChildSheetIds(sheets, child.multi_section))

  return [...children, ...grandChildren]
}

// filterClientEntries :: (Array, Array) -> Array
// Filters out entries that are part of loop or table sheets; those should not be initialized
export const filterClientEntries = (sheets, entries) => {
  const loopSheetIds = sheets.reduce((acc, sheet) => {
    const { sheet_type_id } = sheet
    if (LOOP_SHEET_TYPE_IDS.includes(sheet_type_id)) {
      const childSheetIds = findChildSheetIds(sheets, sheet.multi_section)
      acc.push(sheet.id, ...childSheetIds)
    }
    return acc
  }, [])

  return entries.filter(({ sheet_id }) => !loopSheetIds.includes(sheet_id))
}
